import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Table } from "react-bootstrap";

import { TaskRow } from "./TaskRow";
import { DockMapsLink } from "./DockMapsLink";
import { extractCenterId } from "../../../utils/center";
import { api } from "../../../api";
import { getDryRun, setDryRun } from "../dryRun";
import "../css/DryRunPage.css";

/*
 * Vue dédiée « dry-run » (page à part, ouvrable dans un autre onglet/écran) : compare côte à côte le
 * découpage RÉEL du dispatcheur et celui de NOTRE lib, sur le même périmètre et avec la MÊME formule
 * de coût/retard/avance (comparaison fair). Les lignes commande sont rendues via le composant natif
 * DeliveryRow (task sérialisée par le back, cf. TaskRow), avec repli OrderLine si une task manque.
 */
const DEV = process.env.NODE_ENV !== "production";
const SOURCES = [
  { value: "local", label: "Local (deleev-db)" },
  { value: "preprod", label: "Preprod" },
  { value: "prod", label: "Réplica prod (live)" },
];
const MECHANISMS = [
  { key: "seine_crossing", label: "Traversée Seine" },
  { key: "express_insertion", label: "Insertion express" },
  { key: "consolidation", label: "Consolidation" },
  { key: "repair", label: "Réparation (moindre retard)" },
  { key: "zone_grouping", label: "Regroupement par zone" },  // levier de test : même arrondissement / CP d'abord
];

// Id de snapshot ({centre}-{YYYYMMDDTHHMMSS UTC}-{suffixe}) → libellé local « 23/07 10:50 ».
const snapLabel = (id) => {
  const m = /-(\d{8}T\d{6})-/.exec(id || "");
  if (!m) { return id; }
  const s = m[1];
  const d = new Date(`${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}T${s.slice(9, 11)}:${s.slice(11, 13)}:${s.slice(13, 15)}Z`);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// Pose/retire ?snapshot=<id> dans l'URL (sans navigation) : copier l'URL = partager la vue figée.
const setUrlSnapshot = (id) => {
  const url = new URL(window.location.href);
  if (id) { url.searchParams.set("snapshot", id); } else { url.searchParams.delete("snapshot"); }
  window.history.replaceState(null, "", url.toString());
};

function isParisIntramuros(pc) {
  if (!pc) { return false; }
  const n = parseInt(pc, 10);
  return (n >= 75001 && n <= 75020) || pc === "75116";
}
function dockIsParis(card) {
  return (card.orders || []).length > 0 && (card.orders || []).every((o) => isParisIntramuros(o.postal_code));
}
const r1 = (x) => Math.round((x || 0) * 10) / 10;
const r2 = (x) => Math.round((x || 0) * 100) / 100;
// Heure « HH:MM » d'un ISO, en heure de Paris (le back renvoie de l'UTC).
const hhmmParis = (iso) => {
  if (!iso) { return ""; }
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" });
  } catch (e) {
    return iso.slice(11, 16);
  }
};
// Fenêtre horaire compacte : « 14:30→16:41 », ou la valeur seule quand les bornes coïncident.
const hRange = (a, b) => {
  if (!a || !b) { return b || a; }
  const [lo, hi] = a <= b ? [a, b] : [b, a];  // ordre chronologique (condamnée : 'au plus tard' passé avant 'maintenant')
  return lo !== hi ? `${lo}→${hi}` : lo;
};

function colTotals(cards) {
  return {
    docks: cards.length,
    orders: cards.reduce((s, c) => s + (c.n_orders || 0), 0),
    bags: cards.reduce((s, c) => s + (c.bags || 0), 0),
    time: cards.reduce((s, c) => s + (c.time_min || 0), 0),
    cost: cards.reduce((s, c) => s + (c.cost_eur || 0), 0),
    delay: cards.reduce((s, c) => s + (c.delay_min || 0), 0),
    wait: cards.reduce((s, c) => s + (c.wait_min || 0), 0),
  };
}

function DockCard({ card, tasks, depot }) {
  const [showExplain, setShowExplain] = useState(false);
  const explain = card.explain || [];
  const status = card.status || {};
  return (
    <div className={`DryRun-dock DryRun-dock--${status.code || "unknown"}`}>
      <div className="DryRun-dock-head">
        <b>
          {card.label}
          {card.zone && (
            <span className="DryRun-zone">{card.zone === "paris" ? "Paris" : "Banlieue"}</span>
          )}
          {status.label && (
            <span className={`DryRun-status DryRun-status--${status.code}`}>{status.label}</span>
          )}
          {explain.length > 0 && (
            <button
              className="DryRun-explain-btn"
              title="Pourquoi ce regroupement ?"
              onClick={() => setShowExplain(!showExplain)}
            >ⓘ</button>
          )}
        </b>
        <span>
          {card.n_orders} cmd · {card.bags} sacs · {r1(card.weight_kg)} kg
          {card.unroutable ? (
            <span className="DryRun-unroutable"> · non calculable (tronçon non routable)</span>
          ) : (
            <>
              {" · "}{r2(card.cost_eur)} €
              {card.time_min != null && (
                <span className="DryRun-dur" title="durée totale = chargement des sacs + trajet/service">
                  {" · "}{r1(card.time_min)}m (charg. {r1(card.loading_min)} + trajet {r1(card.route_min)})
                </span>
              )}
              {card.delay_min > 0 && <span className="DryRun-late"> · retard {card.delay_min}m</span>}
              {card.wait_min > 0 && <span className="DryRun-early"> · avance {card.wait_min}m</span>}
            </>
          )}
        </span>
        <DockMapsLink orders={card.orders} tasks={tasks} depot={depot} avoidHighways={card.zone ? card.zone === "paris" : undefined} />
      </div>
      {card.targets && card.targets.depart && (
        card.targets.depart_earliest ? (
          <div className="DryRun-targets" title="Fenêtre d'action : au plus tôt (partir maintenant) → au plus tard (dernier départ sans retard). Retour = dernière commande → entrepôt inclus.">
            <span className="DryRun-targets-tag">fenêtre</span>
            chargement <b>{hRange(card.targets.prep_earliest, card.targets.prep)}</b>
            {" · "}départ <b>{hRange(card.targets.depart_earliest, card.targets.depart)}</b>
            {" · "}retour <b>{hRange(card.targets.return_earliest, card.targets["return"])}</b>
          </div>
        ) : (
          <div className="DryRun-targets" title="Heures au plus tard pour rester à l'heure — chargement = début du chargement des sacs au plus tard ; retour = dernière commande → entrepôt inclus">
            <span className="DryRun-targets-tag">au plus tard</span>
            chargement <b>{card.targets.prep}</b> · départ <b>{card.targets.depart}</b> · retour <b>{card.targets["return"]}</b>
          </div>
        )
      )}
      {showExplain && explain.length > 0 && (
        <ul className="DryRun-explain">
          {explain.map((line, i) => <li key={i}>{line}</li>)}
        </ul>
      )}
      {(card.added_ids || []).length > 0 && (
        <div className="DryRun-added">
          + {card.added_ids.length} ajoutée(s) à ce tick :{" "}
          {(card.orders || []).filter((o) => card.added_ids.includes(o.id)).map((o) => `#${o.ref}`).join(", ")}
        </div>
      )}
      <Table bordered size="sm" hover striped className="DryRun-table">
        <tbody>
          {(card.orders || []).map((o, i) => <TaskRow key={`o${o.ref || i}`} order={o} tasks={tasks} />)}
        </tbody>
      </Table>
    </div>
  );
}

// États de disponibilité (mêmes codes que le back) : servent le filtre d'affichage.
const STATUS_OPTIONS = [
  { code: "ready_go", label: "Prête · à envoyer" },
  { code: "ready_express", label: "Prête · express" },
  { code: "ready_wait", label: "Prête · peut attendre" },
  { code: "prep_urgent", label: "À préparer · urgent" },
  { code: "prep_express", label: "À préparer · express" },
  { code: "prep_wait", label: "À préparer · peut attendre" },
  { code: "unknown", label: "Non calculable" },
];
const ALL_STATUS_CODES = STATUS_OPTIONS.map((s) => s.code);
const SORTS = [
  { value: "slack", label: "Marge" },
  { value: "readiness", label: "Disponibilité" },
  { value: "cost", label: "Coût" },
  { value: "delay", label: "Retard" },
];

// Valeur numérique tolérante : une métrique absente (null) est renvoyée au bout du tri via le fallback.
const num = (v, fallback) => (v == null ? fallback : v);

// Filtre d'affichage : ne garde que les tournées dont l'état est coché (celles sans état restent visibles).
function filterDocks(docks, enabled) {
  return docks.filter((c) => !c.status || !c.status.code || enabled.has(c.status.code));
}

// Tri d'affichage (le back trie déjà par disponibilité ; ici l'utilisateur peut changer le critère).
function sortDocks(docks, sortBy) {
  const arr = [...docks];
  if (sortBy === "cost") {
    arr.sort((a, b) => num(b.cost_eur, -Infinity) - num(a.cost_eur, -Infinity)); // plus cher d'abord
  } else if (sortBy === "delay") {
    arr.sort((a, b) => num(b.delay_min, -Infinity) - num(a.delay_min, -Infinity)); // plus en retard d'abord
  } else if (sortBy === "slack") {
    arr.sort((a, b) => num(a.slack_min, Infinity) - num(b.slack_min, Infinity)); // marge la plus faible d'abord
  } else {
    arr.sort((a, b) => { // disponibilité : rang puis marge croissante (comme le back)
      const ra = (a.status && a.status.rank) || 9;
      const rb = (b.status && b.status.rank) || 9;
      return ra !== rb ? ra - rb : num(a.slack_min, Infinity) - num(b.slack_min, Infinity);
    });
  }
  return arr;
}

// Ligne de totaux d'une colonne (+ sous-total Paris intramuros) : la MÊME des deux côtés, réel et lib,
// pour suivre la comparaison d'efficacité — en direct, elle se recalcule à chaque tick.
function ColumnTotals({ cards }) {
  const tAll = colTotals(cards);
  const tParis = colTotals(cards.filter(dockIsParis));
  return (
    <>
      <div className="DryRun-col-totals">
        <span><b>{tAll.docks}</b> tournées</span>
        <span><b>{tAll.orders}</b> cmd</span>
        <span><b>{tAll.bags}</b> sacs</span>
        <span><b>{r2(tAll.cost)}</b> €</span>
        <span className={tAll.delay > 0 ? "DryRun-late" : ""}>retard <b>{r1(tAll.delay)}</b>m</span>
        <span className={tAll.wait > 0 ? "DryRun-early" : ""}>avance <b>{r1(tAll.wait)}</b>m</span>
      </div>
      <div className="DryRun-col-totals DryRun-col-totals-paris">
        dont Paris intramuros : <b>{tParis.docks}</b> tournées · <b>{tParis.orders}</b> cmd · <b>{tParis.bags}</b> sacs · <b>{r2(tParis.cost)}</b> € · retard <b>{r1(tParis.delay)}</b>m · avance <b>{r1(tParis.wait)}</b>m
      </div>
    </>
  );
}
ColumnTotals.propTypes = { cards: PropTypes.array };

function Column({ title, side, data, tasks, depot, parisOnly, statusFilter, sortBy }) {
  if (!data) {
    return <div className="DryRun-col"><h3>{title}</h3><div className="DryRun-empty">—</div></div>;
  }
  const all = data.docks || [];
  // Totaux = colonne entière (repère stable) ; le filtre/tri n'affecte QUE la liste des cartes affichées.
  const scoped = parisOnly ? all.filter(dockIsParis) : all;
  const displayed = sortDocks(filterDocks(scoped, statusFilter), sortBy);
  return (
    <div className="DryRun-col">
      <h3>{title}</h3>
      <ColumnTotals cards={all} />
      {displayed.map((c, i) => (
        <DockCard key={c.dock_id || c.index || i} card={c} tasks={tasks} depot={depot} />
      ))}
      {side === "prod" && data.loose && data.loose.length > 0 && (
        <div className="DryRun-loose">
          <div className="DryRun-loose-head">À dispatcher ({data.loose.length})</div>
          <div className="DryRun-loose-chips">
            {data.loose.map((o, i) => (
              <span key={i} className={`DryRun-chip${o.is_speed ? " is-speed" : ""}`}>
                {o.postal_code} · {o.bags}s{o.slot_start ? ` · ${o.slot_start}-${o.slot_end}` : ""}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Sous-rubrique repliable de la colonne live (titre + compteur, contenu masquable).
function LiveSection({ title, count, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="DryRun-live-sec">
      <button type="button" className="DryRun-live-section" onClick={() => setOpen(!open)}>
        <span className="DryRun-live-caret">{open ? "▾" : "▸"}</span> {title} ({count})
      </button>
      {open && children}
    </div>
  );
}
LiveSection.propTypes = { title: PropTypes.string, count: PropTypes.number, defaultOpen: PropTypes.bool, children: PropTypes.node };

// LIVE (observation continue), rendu comme une colonne native qui ÉVOLUE à chaque tick, en 4
// sous-rubriques repliables qui suivent le cycle de vie théorique d'une tournée de la lib :
// « en cours de création » (formée, pas encore due) → « gelée » (figée, en prépa ou prête) →
// « en livraison » (départ visé passé) → « livrée » (retour visé passé). Les tournées parties sont
// gardées par l'écran (le serveur ne peut plus les retrouver une fois leurs commandes réellement
// livrées) ; leurs lignes natives restent rafraîchies à chaque tick (état réel). Zéro écriture.
function LiveColumn({ session }) {
  const last = session.last || {};
  const s = last.situations || {};
  const frozen = session.openCards || [];
  const pending = session.pendingCards || [];
  const ref = last.at ? new Date(last.at) : new Date();
  const gone = session.goneCards || [];
  const delivered = gone.filter((c) => c.return_at && new Date(c.return_at) <= ref);
  const delivering = gone.filter((c) => !(c.return_at && new Date(c.return_at) <= ref));
  const cards = (list, prefix) => list.map((c, i) => (
    <DockCard key={`${prefix}${c.uid != null ? c.uid : i}`} card={c} tasks={session.tasks} depot={session.depot} />
  ));
  // Population COMPARABLE à la colonne réelle : le périmètre réel exclut les commandes parties
  // (SHIPPING) et livrées (RECEIVED) → il ne montre que ce qui est AU QUAI (brouillons + prêtes). Le
  // total lib compte donc gelées + en création, et rien d'autre — même population, même formule.
  // Les tournées parties (projection théorique de la lib) sont rappelées à part, jamais dans ce total.
  const comparable = [...frozen, ...pending];
  const tGone = colTotals(gone);  // `gone` = toutes les tournées parties (livrées + en livraison), déjà déclaré
  return (
    <div className="DryRun-col">
      <h3>
        Notre lib (auto) — en direct
        {session.running ? <span className="DryRun-live-dot" /> : <span className="DryRun-live-stopped"> · arrêté</span>}
        {last.at && <span className="DryRun-live-when"> · tick {hhmmParis(last.at)}</span>}
        {last.busy && <span className="DryRun-live-when"> · calcul en cours…</span>}
      </h3>
      <ColumnTotals cards={comparable} />
      {gone.length > 0 && (
        <div className="DryRun-live-gone" title="Hors du total ci-dessus : le périmètre réel ne montre plus ces commandes une fois parties. Projection théorique de la lib.">
          + parties (théorique) : <b>{tGone.docks}</b> tournées · <b>{tGone.orders}</b> cmd · <b>{r2(tGone.cost)}</b> €
        </div>
      )}
      <div className="DryRun-live-status">
        <span>livrées <b>{delivered.length}</b></span>
        <span>en livraison <b>{delivering.length}</b></span>
        <span>gelées <b>{frozen.length}</b></span>
        <span>en création <b>{pending.length}</b></span>
        <span>pool restant <b>{last.pool != null ? last.pool : "—"}</b></span>
        {last.freeze_mode && (
          <span className={`DryRun-freeze DryRun-freeze--${last.freeze_mode}`}>
            {last.freeze_mode === "latest" ? "Gel au plus tard" : "Gel au plus tôt"}
          </span>
        )}
        {s.drivers_short && <span className="DryRun-tag DryRun-tag--alert">manque de livreurs</span>}
        {s.preparers_short && <span className="DryRun-tag DryRun-tag--alert">prépa en retard</span>}
        {s.cold_room_full && <span className="DryRun-tag DryRun-tag--alert">chambre froide pleine</span>}
        {last.error && <span className="DryRun-error">{last.error}</span>}
      </div>
      <LiveSection title="Tournées livrées" count={delivered.length} defaultOpen={false}>
        {delivered.length === 0 ? <div className="DryRun-live-empty">Aucune tournée livrée pour l’instant.</div> : cards(delivered, "d")}
      </LiveSection>
      <LiveSection title="En livraison" count={delivering.length} defaultOpen>
        {delivering.length === 0 ? <div className="DryRun-live-empty">Aucune tournée en livraison.</div> : cards(delivering, "l")}
      </LiveSection>
      <LiveSection title="Tournées gelées (en prépa / prêtes à partir)" count={frozen.length} defaultOpen>
        {frozen.length === 0 ? <div className="DryRun-live-empty">Rien de gelé pour l’instant (aucune tournée n’a atteint son départ).</div> : cards(frozen, "f")}
      </LiveSection>
      <LiveSection title="En cours de création" count={pending.length} defaultOpen>
        {pending.length === 0 ? <div className="DryRun-live-empty">Aucune tournée en formation à ce tick.</div> : cards(pending, "p")}
      </LiveSection>
    </div>
  );
}
LiveColumn.propTypes = { session: PropTypes.object };

function DryRunPage({ centerId }) {
  const [source, setSource] = useState(getDryRun().source);
  const [day, setDay] = useState(getDryRun().day || new Date().toISOString().slice(0, 10));  // jour courant par défaut
  const [parisOnly, setParisOnly] = useState(true);
  const [showProd, setShowProd] = useState(true);  // colonne dispatcheur réel affichée par défaut
  const [dockedOnly, setDockedOnly] = useState(true);  // comparaison à même population par défaut
  const [flags, setFlags] = useState({
    seine_crossing: true, express_insertion: true, consolidation: true, repair: true, zone_grouping: false,
  });
  const [maxWait, setMaxWait] = useState(30);
  const [maxDelay, setMaxDelay] = useState(15);   // retard ajouté toléré (min) par le regroupement
  // Montant visé par tournée et par zone (optionnel) : ce qu'on souhaite qu'une tournée coûte.
  // Décoché = aucun objectif, seule la consolidation regroupe.
  const [minTourOn, setMinTourOn] = useState(true);  // gain minimum scooter actif par défaut (config lib)
  const [minTourParis, setMinTourParis] = useState(18);
  const [minTourOther, setMinTourOther] = useState(18);
  const [urgentSlack, setUrgentSlack] = useState(15);  // préavis d'urgence des étiquettes (min), distinct du retard toléré
  const [maxBagsParis, setMaxBagsParis] = useState(16);   // scooter (Paris intramuros)
  const [maxWeightParis, setMaxWeightParis] = useState(80);
  const [maxBagsOther, setMaxBagsOther] = useState(40);   // véhicule plus grand (banlieue)
  const [maxWeightOther, setMaxWeightOther] = useState(250);
  // Bulle (km) : diamètre max du disque contenant les arrêts d'une tournée. 0 = pas de contrainte (levier de test).
  const [bubbleParis, setBubbleParis] = useState(0);
  const [bubbleOther, setBubbleOther] = useState(0);
  // Arc (°) : ouverture max du secteur angulaire, vu du dépôt, contenant les arrêts d'une tournée. 0 = sans.
  const [arcParis, setArcParis] = useState(0);
  const [arcOther, setArcOther] = useState(0);
  // Découpage de Paris par secteur (levier de test) : "off" = aucun (défaut), "seine16" = sud + 16e / nord.
  const [sectorParis, setSectorParis] = useState("seine16");  // découpage Sud+16 / Nord actif par défaut (config lib)
  const [statusFilter, setStatusFilter] = useState(new Set(ALL_STATUS_CODES));  // filtre par disponibilité
  const [sortBy, setSortBy] = useState("slack");  // critère de tri d'affichage (marge : tout ce qui brûle en tête)
  // Vue figée : id de snapshot rejoué (depuis l'URL ?snapshot=…, un lien partagé, ou « récents »).
  const [viewSnapshot, setViewSnapshot] = useState(
    () => new URLSearchParams(window.location.search).get("snapshot") || ""
  );
  const [recents, setRecents] = useState([]);  // snapshots récents du centre (ids)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Live continu (observation) : l'écran ticke en avant depuis maintenant, garde l'état gelé en mémoire.
  const [liveSession, setLiveSession] = useState(null);
  const [liveStepMin, setLiveStepMin] = useState(1);  // cadence auto des ticks (min)
  const liveSessionRef = useRef(null);   // miroir de la session lu par le timer (closures fraîches)
  const liveTimerRef = useRef(null);     // handle du setInterval d'auto-tick
  const liveInFlightRef = useRef(false); // un tick tourne en tâche de fond : l'auto-tick n'en empile pas un 2e
  const liveSeqRef = useRef(0);          // n° de session : un sondage d'une session remplacée est ignoré
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  // Jeton du chargement en cours : seule la réponse du DERNIER `Charger` est appliquée. Neutralise la
  // course de deux chargements rapprochés (double-clic, ou clic pendant qu'une réponse est encore en vol)
  // qui, sinon, se marchent dessus et affichent un résultat incohérent.
  const loadSeqRef = useRef(0);

  const toggleStatus = (code) => {
    const next = new Set(statusFilter);
    if (next.has(code)) { next.delete(code); } else { next.add(code); }
    setStatusFilter(next);
  };

  const refreshRecents = () => {
    if (centerId) { api.listSnapshots(centerId).then(setRecents); }
  };

  // Sonde un job de comparaison (2 s) jusqu'au résultat : le calcul tourne en tâche de fond côté
  // back (il dépasse le timeout d'une requête web), la barre suit `progress` (0-100).
  const pollCompare = (jobId, fresh, onDone, onErr, attempt = 0) => {
    if (!fresh()) { return; }
    if (attempt > 200) { onErr(); return; }  // ~7 min : le job est planté, on rend la main
    api.compareStatus(jobId)
      .then((st) => {
        if (!fresh()) { return; }
        if (st.status === "failed" || st.status === "unknown") { onErr(); return; }
        setProgress(st.progress || 0);
        if (st.status === "finished" && st.result) { onDone(st.result); return; }
        setTimeout(() => pollCompare(jobId, fresh, onDone, onErr, attempt + 1), 2000);
      })
      .catch(() => setTimeout(() => pollCompare(jobId, fresh, onDone, onErr, attempt + 1), 3000));
  };

  // Met à jour la session live (état + miroir ref lu par le timer).
  const setSession = (updater) => setLiveSession((prev) => {
    const next = typeof updater === "function" ? updater(prev) : updater;
    liveSessionRef.current = next;
    return next;
  });

  // Applique le rapport d'un tick à la session : reporte l'état gelé, accumule les parties, etc.
  const applyTick = (r) => setSession((prev) => {
    if (!prev) { return prev; }
    const openCards = r.open || [];
    // État gelé slim reporté au tick suivant (le back n'a besoin que des ids + départ/retour par tournée).
    const seeds = openCards.map((c) => ({ uid: c.uid, order_ids: (c.orders || []).map((o) => o.id), depart_at: c.depart_at, return_at: c.return_at }));
    // Tournée gelée RATTRAPÉE PAR LE RÉEL : le dispatcheur a livré toutes ses commandes avant son départ
    // théorique → le serveur ne peut plus la reconstruire (plus rien dans le pool) et elle disparaît de
    // `open` sans passer par `departed`. On garde NOTRE carte (l'écran l'a) et on la classe « livrée »,
    // badgée — sinon elle s'évaporerait et les rubriques ne montreraient que les départs théoriques.
    const kept = new Set([...openCards, ...(r.departed || [])].map((c) => c.uid));
    const caughtByReal = (prev.openCards || [])
      .filter((c) => !kept.has(c.uid))
      .map((c) => ({ ...c, return_at: r.at, caught_by_real: true, added_ids: [], status: { code: "ready_go", label: "livrée par le réel", rank: 0 } }));
    return {
      ...prev,
      seeds,
      openCards,                      // tournées GELÉES (persistées d'un tick au suivant)
      pendingCards: r.pending || [],  // tournées EN CRÉATION (recalculées à chaque tick)
      goneCards: [...(prev.goneCards || []), ...(r.departed || []), ...caughtByReal],  // parties : gardées par l'écran
      prodLive: r.prod || prev.prodLive,  // découpage RÉEL du dispatcheur à l'instant du tick
      tasks: r.tasks || {},
      depot: r.depot,
      nextUid: r.next_uid != null ? r.next_uid : prev.nextUid,
      last: { at: r.at, situations: r.situations, freeze_mode: r.freeze_mode, source: r.source, pool: r.pool, error: r.error, busy: false },
    };
  });
  const failTick = (msg) => setSession((prev) => (prev ? { ...prev, last: { ...(prev.last || {}), error: msg, busy: false } } : prev));

  // Sonde le job d'un tick (2 s) jusqu'au rapport ; un tick dure 60-90 s sur un vrai pool.
  const pollTick = (jobId, seq, attempt = 0) => {
    if (seq !== liveSeqRef.current) { liveInFlightRef.current = false; return; }  // session remplacée
    if (attempt > 300) { liveInFlightRef.current = false; failTick("Tick trop long (abandon)"); return; }
    api.liveTickStatus(jobId)
      .then((st) => {
        if (seq !== liveSeqRef.current) { liveInFlightRef.current = false; return; }
        if (st.status === "failed" || st.status === "unknown") { liveInFlightRef.current = false; failTick("Échec du tick"); return; }
        if (st.status === "finished" && st.result) { liveInFlightRef.current = false; applyTick(st.result); return; }
        setTimeout(() => pollTick(jobId, seq, attempt + 1), 2000);
      })
      .catch(() => setTimeout(() => pollTick(jobId, seq, attempt + 1), 3000));
  };

  // UN tick : lance le job (gèle/ajoute à l'instant présent + côté réel) puis sonde. Jamais deux en vol.
  const doLiveTick = () => {
    const cur = liveSessionRef.current;
    if (!cur || liveInFlightRef.current) { return; }
    liveInFlightRef.current = true;
    const seq = liveSeqRef.current;
    setSession((prev) => (prev ? { ...prev, last: { ...(prev.last || {}), busy: true } } : prev));
    const goneOrderIds = (cur.goneCards || []).flatMap((c) => (c.orders || []).map((o) => o.id));
    api.liveTickStart(centerId, { source, stepMin: liveStepMin, seedsState: cur.seeds, nextUid: cur.nextUid, goneOrderIds })
      .then((res) => pollTick(res.job_id, seq))
      .catch(() => { liveInFlightRef.current = false; failTick("Échec du tick"); });
  };

  const stopLive = () => {
    if (liveTimerRef.current) { clearInterval(liveTimerRef.current); liveTimerRef.current = null; }
    setSession((prev) => (prev ? { ...prev, running: false } : prev));
  };

  // Démarre le live : re-tick tout seul toutes les X min, en gardant l'état gelé en mémoire.
  const startLive = () => {
    if (liveTimerRef.current) { clearInterval(liveTimerRef.current); }
    liveSeqRef.current += 1; liveInFlightRef.current = false;
    setSession({ running: true, startedAt: new Date().toISOString(), seeds: [], openCards: [], pendingCards: [], goneCards: [], prodLive: null, tasks: {}, depot: null, nextUid: 0, last: null });
    setTimeout(doLiveTick, 0);  // 1er tick une fois la ref posée
    liveTimerRef.current = setInterval(doLiveTick, Math.max(1, liveStepMin) * 60000);
  };

  // Un tick manuel, sans attendre l'auto (démarre une session à l'arrêt si besoin).
  const tickNow = () => {
    if (!liveSessionRef.current) {
      liveSeqRef.current += 1; liveInFlightRef.current = false;
      setSession({ running: false, startedAt: new Date().toISOString(), seeds: [], openCards: [], pendingCards: [], goneCards: [], prodLive: null, tasks: {}, depot: null, nextUid: 0, last: null });
    }
    setTimeout(doLiveTick, 0);
  };

  // Coupe l'auto-tick si l'écran est démonté.
  useEffect(() => () => { if (liveTimerRef.current) { clearInterval(liveTimerRef.current); } }, []);

  // Lance le comparatif en tâche de fond puis sonde ; API sans les endpoints async → voie synchrone.
  const runCompare = (options, fresh, onDone, onErr) => {
    setProgress(0);
    api.compareStart(centerId, options)
      .then((res) => pollCompare(res.job_id, fresh, onDone, onErr))
      .catch(() => api.compareDocks(centerId, options).then((d) => { if (fresh()) { onDone(d); } })
        .catch(() => { if (fresh()) { onErr(); } }));
  };

  // Charge la vue sélectionnée (live ou figée). Seul déclencheur : le bouton (ou l'arrivée par URL).
  const load = (snapId = viewSnapshot) => {
    const seq = ++loadSeqRef.current;                 // ce chargement devient le seul « courant »
    const fresh = () => seq === loadSeqRef.current;    // une réponse plus récente l'a-t-elle supplanté ?
    setLoading(true); setError(null);
    if (snapId) {
      // Replay d'une vue figée : l'entrée (réglages compris) vient du snapshot — on n'envoie rien d'autre.
      runCompare({ snapshot: snapId }, fresh,
        (d) => { setData(d); setLoading(false); setUrlSnapshot(snapId); },
        () => { setError("Snapshot introuvable ou expiré"); setLoading(false); });
      return;
    }
    setDryRun({ source, day });
    const options = {
      source, max_wait_min: maxWait, max_delay_min: maxDelay, urgent_slack_min: urgentSlack,
      max_bags_paris: maxBagsParis, max_weight_paris: maxWeightParis,
      max_bags_other: maxBagsOther, max_weight_other: maxWeightOther,
      bubble_km_paris: bubbleParis || 0, bubble_km_other: bubbleOther || 0,
      arc_deg_paris: arcParis || 0, arc_deg_other: arcOther || 0,
      sector_paris: sectorParis || "",
      min_tour_eur_paris: minTourOn ? minTourParis : 0,
      min_tour_eur_other: minTourOn ? minTourOther : 0,
    };
    if (day) { options.day = day; }
    MECHANISMS.forEach((m) => { options[m.key] = flags[m.key] ? 1 : 0; });
    options.docked_only = dockedOnly ? 1 : 0;  // même population : commandes déjà en dock des deux côtés
    runCompare(options, fresh,
      (d) => {
        setData(d); setLoading(false);
        // La consultation vient d'être figée côté back : l'URL porte son id → copier l'URL = la partager.
        if (d.snapshot_id) { setUrlSnapshot(d.snapshot_id); }
        refreshRecents();
      },
      () => { setError("Échec du calcul de la lib"); setLoading(false); });
  };

  // Lien partagé (?snapshot=…) : charge la vue figée dès l'arrivée sur la page.
  useEffect(() => {
    refreshRecents();
    if (viewSnapshot) { load(viewSnapshot); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewingSnapshot = data && typeof data.source === "string" && data.source.startsWith("snapshot:");
  // Zones automatisées : si elles ne couvrent pas les deux, les totaux des deux colonnes portent
  // des populations différentes (le dispatcheur traite tout, la lib une partie).
  const zones = (data && data.zones) || null;
  // État du centre à l'instant de la consultation : ce que le live viserait s'il tournait
  // maintenant. Le dry-run ne fige rien, il donne à lire.
  const situations = (data && data.situations) || null;
  const freezeMode = (data && data.freeze_mode) || "earliest";
  const zonesPartial = !!zones && !(zones.includes("paris") && zones.includes("other"));
  const zoneLabels = zones
    ? zones.map((z) => (z === "paris" ? "Paris intramuros" : "la banlieue")).join(" et ")
    : "";
  const prodTotals = (data && data.prod && data.prod.totals) || {};
  const outOfZoneDocks = prodTotals.out_of_zone_docks || 0;
  const outOfZoneOrders = prodTotals.out_of_zone_orders || 0;
  // Commandes que le dispatcheur n'a pas encore groupées : l'autre moitié de l'asymétrie.
  const looseCount = prodTotals.loose || 0;

  const columnsShown = 1 + (showProd ? 1 : 0);

  return (
    <div className="DryRun-page">
      {/* En vue figée sélectionnée, les réglages sont estompés : le replay utilise ceux du snapshot. */}
      <div className={`DryRun-controls${viewSnapshot ? " DryRun-controls--replay" : ""}`}>
        <strong>Comparateur dry-run</strong>
        {DEV && (
          <label>
            <span className="DryRun-devtag">dev</span> Source
            <select value={source} onChange={(e) => setSource(e.target.value)}>
              {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
        )}
        <label>Jour <input type="date" value={day} onChange={(e) => setDay(e.target.value)} /></label>
        <label><input type="checkbox" checked={parisOnly} onChange={(e) => setParisOnly(e.target.checked)} /> Paris intramuros</label>
        <label title="Ne comparer que les commandes déjà dans un dock du dispatcheur : même population des deux côtés (exclut le vrac « à dispatcher », les non préparées et le lendemain non confié).">
          <input type="checkbox" checked={dockedOnly} onChange={(e) => setDockedOnly(e.target.checked)} /> Commandes déjà en dock
        </label>
        <label title="Colonne du dispatcheur réel (ses tournées du jour), chiffrée par la même formule.">
          <input type="checkbox" checked={showProd} onChange={(e) => setShowProd(e.target.checked)} /> Dispatcheur (réel)
        </label>
        {MECHANISMS.map((m) => (
          <label key={m.key} title={m.label}>
            <input type="checkbox" checked={flags[m.key]} onChange={(e) => setFlags({ ...flags, [m.key]: e.target.checked })} />
            {m.label}
          </label>
        ))}
        <label>Scooter (Paris) sacs <input type="number" min="1" value={maxBagsParis} onChange={(e) => setMaxBagsParis(Number(e.target.value))} /></label>
        <label>kg <input type="number" min="1" value={maxWeightParis} onChange={(e) => setMaxWeightParis(Number(e.target.value))} /></label>
        <label>Banlieue sacs <input type="number" min="1" value={maxBagsOther} onChange={(e) => setMaxBagsOther(Number(e.target.value))} /></label>
        <label>kg <input type="number" min="1" value={maxWeightOther} onChange={(e) => setMaxWeightOther(Number(e.target.value))} /></label>
        <label title="Bulle : tous les arrêts d’une tournée tiennent dans un disque de ce diamètre (km). 0 = pas de contrainte.">
          Bulle scooter <input type="number" min="0" step="0.5" value={bubbleParis} onChange={(e) => setBubbleParis(Number(e.target.value))} /> km
        </label>
        <label title="Bulle voiture (km). 0 = pas de contrainte.">
          voiture <input type="number" min="0" step="0.5" value={bubbleOther} onChange={(e) => setBubbleOther(Number(e.target.value))} /> km
        </label>
        <label title="Arc : vus du dépôt, tous les arrêts d’une tournée tiennent dans un secteur de cette ouverture (°). Borne le zigzag, pas la profondeur. 0 = pas de contrainte.">
          Arc scooter <input type="number" min="0" step="5" value={arcParis} onChange={(e) => setArcParis(Number(e.target.value))} /> °
        </label>
        <label title="Arc voiture (°). 0 = pas de contrainte.">
          voiture <input type="number" min="0" step="5" value={arcOther} onChange={(e) => setArcOther(Number(e.target.value))} /> °
        </label>
        <label title="Découpage de Paris en secteurs : une tournée ne mélange jamais deux secteurs.">
          Secteurs Paris{" "}
          <select value={sectorParis} onChange={(e) => setSectorParis(e.target.value)}>
            <option value="off">aucun</option>
            <option value="seine16">Sud + 16e | Nord</option>
            <option value="rives">Rive gauche | Rive droite</option>
          </select>
        </label>
        <label>Attente max <input type="number" min="0" value={maxWait} onChange={(e) => setMaxWait(Number(e.target.value))} /></label>
        <label title="Retard ajouté toléré par le regroupement (min) : les leviers peuvent grouper jusqu'à ce retard total ajouté par tournée, pas seulement à zéro. Pilote aussi la marge de sécurité bolt/quick+.">Retard toléré <input type="number" min="0" value={maxDelay} onChange={(e) => setMaxDelay(Number(e.target.value))} /></label>
        <label title="Une tournée passe « à envoyer » / « à préparer · urgent » quand sa marge de départ tombe sous ce seuil (min). C'est le temps de réaction qu'on se laisse (finir la prépa, charger, trouver un livreur), indépendant du retard toléré.">Urgent si marge &lt; <input type="number" min="0" value={urgentSlack} onChange={(e) => setUrgentSlack(Number(e.target.value))} /> min</label>
        <label title="Gain minimum qu'on souhaite pour le livreur sur une tournée : comparé au versé (minimum garanti du barème inclus), il pousse la construction à densifier les tournées qui rapportent moins. Décoche pour n'appliquer aucun objectif.">
          <input type="checkbox" checked={minTourOn} onChange={(e) => setMinTourOn(e.target.checked)} /> Tournée visée
          <input type="number" min="0" step="0.5" value={minTourParis} disabled={!minTourOn} onChange={(e) => setMinTourParis(Number(e.target.value))} title="Scooter Paris" /> €&nbsp;scooter
          <input type="number" min="0" step="0.5" value={minTourOther} disabled={!minTourOn} onChange={(e) => setMinTourOther(Number(e.target.value))} title="Voiture banlieue" /> €&nbsp;voiture
        </label>
        {/* Choix de la vue (sans effet immédiat) : le bouton est le SEUL déclencheur de chargement. */}
        <label className="DryRun-viewsel" title="Live = calcul du moment avec les réglages ci-contre. Vue figée = rejoue exactement une consultation passée (réglages figés compris) — chaque chargement live en crée une.">
          Vue
          <select value={viewSnapshot} onChange={(e) => setViewSnapshot(e.target.value)}>
            <option value="">Live (direct)</option>
            {recents.map((id) => <option key={id} value={id}>Figée · {snapLabel(id)}</option>)}
          </select>
        </label>
        <button className="DryRun-load" onClick={() => load()} disabled={loading}>
          {loading ? `Calcul… ${progress}%` : (viewSnapshot ? "Rejouer" : "Charger")}
        </button>
        <button className="DryRun-live-btn" title="Live continu : le dry-run ticke en avant depuis maintenant, gèle et ajoute tout seul toutes les X min en gardant l'état gelé en mémoire — sans rien écrire. C'est le vrai comportement live, observé."
          onClick={liveSession && liveSession.running ? stopLive : startLive}>
          {liveSession && liveSession.running ? "Arrêter le live" : "Démarrer le live"}
        </button>
        <button className="DryRun-live-btn" title="Avance d'un tick tout de suite, sans attendre l'auto."
          onClick={tickNow}>Tick maintenant</button>
        <label className="DryRun-live-step" title="Cadence de l'auto-tick (minutes).">
          toutes les
          <input type="number" min="1" max="60" value={liveStepMin}
            onChange={(e) => setLiveStepMin(Math.max(1, parseInt(e.target.value, 10) || 1))} /> min
        </label>
        {error && <span className="DryRun-error">{error}</span>}
      </div>

      <div className="DryRun-viewbar">
        <span className="DryRun-filter">
          Afficher :
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.code}
              type="button"
              className={`DryRun-fchip DryRun-fchip--${s.code}${statusFilter.has(s.code) ? " on" : ""}`}
              onClick={() => toggleStatus(s.code)}
              title={statusFilter.has(s.code) ? "Cliquer pour masquer" : "Cliquer pour afficher"}
            >{s.label}</button>
          ))}
        </span>
        <label>Trier
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
      </div>

      {viewingSnapshot && (
        <div className="DryRun-frozen">
          Vue figée du <b>{snapLabel(viewSnapshot) || data.source.replace("snapshot:", "")}</b> — l’URL de
          cette page rejoue exactement cette vue (partageable).
          <span className="DryRun-frozen-hint">Retour au direct : Vue « Live » puis Charger.</span>
        </div>
      )}

      {data && data.diagnostic && (
        <div className="DryRun-diag">⚠ {data.diagnostic}</div>
      )}

      {situations && (
        <div className="DryRun-situ">
          <span className={`DryRun-freeze DryRun-freeze--${freezeMode}`}>
            {freezeMode === "latest" ? "Gel au plus tard" : "Gel au plus tôt"}
          </span>
          <span className="DryRun-situ-why">
            {freezeMode === "latest"
              ? "on vise les échéances : les tournées restent modifiables le plus longtemps possible"
              : "on vise l’ouverture des créneaux : on livre dès que les clients attendent"}
          </span>
          <span className="DryRun-situ-tags">
            {situations.drivers_short && <span className="DryRun-tag DryRun-tag--alert">manque de livreurs</span>}
            {situations.preparers_short && <span className="DryRun-tag DryRun-tag--alert">prépa en retard</span>}
            {situations.cold_room_full && <span className="DryRun-tag DryRun-tag--alert">chambre froide pleine</span>}
            {!situations.drivers_short && !situations.preparers_short && !situations.cold_room_full && (
              <span className="DryRun-tag">rien à signaler</span>
            )}
          </span>
        </div>
      )}


      {zonesPartial && !parisOnly && (
        <div className="DryRun-zonewarn">
          <b>Volumes non comparables</b> — pour deux raisons cumulées, aucune n’étant une différence
          de performance :
          <ul className="DryRun-zonewarn-list">
            <li>
              <b>Zones</b> : le dispatch auto n’est activé que pour {zoneLabels}. Le dispatcheur
              traite tout le périmètre, dont <b>{outOfZoneDocks}</b> tournée
              {outOfZoneDocks > 1 ? "s" : ""} et <b>{outOfZoneOrders}</b> commande
              {outOfZoneOrders > 1 ? "s" : ""} que la lib n’avait pas le droit de construire.
            </li>
            <li>
              <b>Avancement</b> : le dispatcheur n’a groupé qu’une partie des commandes à cet
              instant — les autres sont encore dans son vrac « à dispatcher »
              {looseCount > 0 ? ` (${looseCount})` : ""}. La lib, elle, groupe tout le pool d’un
              coup.
            </li>
          </ul>
          Ce sont les <b>tournées</b> qu’il faut comparer (leur remplissage, leur coût), pas les
          totaux de volume.{" "}
          <button type="button" className="DryRun-zonewarn-cta" onClick={() => setParisOnly(true)}>
            Se limiter à Paris intramuros
          </button>
        </div>
      )}

      {/* Live actif : le dispatcheur réel chargé reste à gauche comme repère, la lib EN DIRECT
          (gelé / à geler, évolue à chaque tick) est à droite. Sinon, le comparatif habituel. */}
      {liveSession ? (
        <div className="DryRun-cols" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
          <Column
            title={liveSession.prodLive ? "Dispatcheur (réel) — en direct" : "Dispatcheur (réel)"}
            side="prod"
            data={liveSession.prodLive || (data && data.prod)}
            tasks={liveSession.prodLive ? liveSession.tasks : (data && data.tasks)}
            depot={liveSession.depot || (data && data.depot)}
            parisOnly={parisOnly} statusFilter={statusFilter} sortBy={sortBy}
          />
          <LiveColumn session={liveSession} />
        </div>
      ) : (
        <div className="DryRun-cols" style={{ gridTemplateColumns: `repeat(${columnsShown}, 1fr)` }}>
          {showProd && (
            <Column title="Dispatcheur (réel)" side="prod" data={data && data.prod} tasks={data && data.tasks} depot={data && data.depot} parisOnly={parisOnly} statusFilter={statusFilter} sortBy={sortBy} />
          )}
          <Column title="Notre lib (auto)" side="lib" data={data && data.lib} tasks={data && data.tasks} depot={data && data.depot} parisOnly={parisOnly} statusFilter={statusFilter} sortBy={sortBy} />
        </div>
      )}
    </div>
  );
}

DockCard.propTypes = { card: PropTypes.object, tasks: PropTypes.object, depot: PropTypes.object };
Column.propTypes = { title: PropTypes.string, side: PropTypes.string, data: PropTypes.object, tasks: PropTypes.object, depot: PropTypes.object, parisOnly: PropTypes.bool, statusFilter: PropTypes.object, sortBy: PropTypes.string };
DryRunPage.propTypes = { centerId: PropTypes.number };

export default connect(
  (state) => ({ centerId: extractCenterId(state.user) }),
)(DryRunPage);
